import React, { useRef, useEffect, useState } from 'react';
import { IconButton, Tooltip, Paper } from '@mui/material';
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import { Core } from 'cytoscape';

interface GraphMiniMapProps {
  cyInstance: Core | null;
  width?: number;
  height?: number;
  visible?: boolean;
  onVisibilityChange?: (visible: boolean) => void;
}

/**
 * GraphMiniMap Component
 *
 * Provides a bird's-eye view of the entire knowledge graph with a viewport indicator.
 * Helps users navigate large graphs by showing their current position and allowing
 * click-to-navigate functionality.
 *
 * Features:
 * - Real-time viewport tracking (updates at 60fps)
 * - Click-to-navigate functionality
 * - Simplified node rendering (colored dots)
 * - Current viewport rectangle overlay
 * - Toggle show/hide with localStorage persistence
 * - Adaptive sizing based on graph bounds
 */
const GraphMiniMap: React.FC<GraphMiniMapProps> = ({
  cyInstance,
  width = 200,
  height = 150,
  visible: controlledVisible,
  onVisibilityChange,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isVisible, setIsVisible] = useState(() => {
    // Load visibility preference from localStorage
    const saved = localStorage.getItem('graphMiniMapVisible');
    return saved !== null ? saved === 'true' : true;
  });

  // Use controlled visibility if provided, otherwise use local state
  const visible = controlledVisible !== undefined ? controlledVisible : isVisible;

  // Handle visibility toggle
  const handleToggleVisibility = () => {
    const newVisible = !visible;
    setIsVisible(newVisible);
    localStorage.setItem('graphMiniMapVisible', String(newVisible));
    onVisibilityChange?.(newVisible);
  };

  // Render mini-map
  useEffect(() => {
    if (!canvasRef.current || !cyInstance || !visible) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;

    const renderMiniMap = () => {
      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Get graph bounds
      const boundingBox = cyInstance.elements().boundingBox();
      const graphWidth = boundingBox.x2 - boundingBox.x1 || 1;
      const graphHeight = boundingBox.y2 - boundingBox.y1 || 1;

      // Calculate scale to fit graph in mini-map
      const scaleX = width / graphWidth;
      const scaleY = height / graphHeight;
      const scale = Math.min(scaleX, scaleY) * 0.9; // 90% to add padding

      // Calculate offset to center graph in mini-map
      const offsetX = (width - graphWidth * scale) / 2 - boundingBox.x1 * scale;
      const offsetY = (height - graphHeight * scale) / 2 - boundingBox.y1 * scale;

      // Draw background
      ctx.fillStyle = '#F5F5F5';
      ctx.fillRect(0, 0, width, height);

      // Draw border
      ctx.strokeStyle = '#CCCCCC';
      ctx.lineWidth = 1;
      ctx.strokeRect(0, 0, width, height);

      // Draw all nodes as small circles
      cyInstance.nodes().forEach(node => {
        // Skip hidden nodes
        if (node.hasClass('hidden')) return;

        const pos = node.position();
        const x = pos.x * scale + offsetX;
        const y = pos.y * scale + offsetY;

        // Get node color based on type or default
        let nodeColor = '#4056A6'; // Default color
        const type = node.data('type');

        // Match colors from cytoscape-config
        const typeColors: Record<string, string> = {
          message: '#4056A6',
          document: '#325BA8',
          concept: '#4DC4D2',
          person: '#892987',
          topic: '#E94257',
          organization: '#999999',
          email: '#FF9800',
          knowledge: '#4DC4D2',
          conversation: '#325BA8',
        };

        nodeColor = typeColors[type] || nodeColor;

        // Highlight search matches
        if (node.hasClass('search-match')) {
          nodeColor = '#FFD700'; // Gold
        }

        // Highlight selected nodes
        if (node.hasClass('selected')) {
          nodeColor = '#E94257'; // Coral Red
        }

        // Draw node
        ctx.fillStyle = nodeColor;
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, 2 * Math.PI);
        ctx.fill();
      });

      // Draw viewport rectangle
      const extent = cyInstance.extent();
      const viewportX1 = extent.x1 * scale + offsetX;
      const viewportY1 = extent.y1 * scale + offsetY;
      const viewportX2 = extent.x2 * scale + offsetX;
      const viewportY2 = extent.y2 * scale + offsetY;
      const viewportWidth = viewportX2 - viewportX1;
      const viewportHeight = viewportY2 - viewportY1;

      // Draw viewport rectangle with fill
      ctx.fillStyle = 'rgba(64, 86, 166, 0.1)'; // Light blue fill
      ctx.fillRect(viewportX1, viewportY1, viewportWidth, viewportHeight);

      // Draw viewport rectangle border
      ctx.strokeStyle = '#E94257'; // Coral Red
      ctx.lineWidth = 2;
      ctx.strokeRect(viewportX1, viewportY1, viewportWidth, viewportHeight);

      // Draw node count in corner
      const nodeCount = cyInstance.nodes(':visible').length;
      ctx.fillStyle = '#333333';
      ctx.font = '10px Roboto, sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillText(`${nodeCount} nodes`, width - 5, height - 5);
    };

    // Initial render
    renderMiniMap();

    // Update on viewport changes (pan/zoom)
    const handleViewportChange = () => {
      // Use requestAnimationFrame for smooth 60fps updates
      cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(renderMiniMap);
    };

    cyInstance.on('viewport', handleViewportChange);
    cyInstance.on('add remove', renderMiniMap); // Re-render when nodes/edges change
    cyInstance.on('data', renderMiniMap); // Re-render when data changes (e.g., class changes)

    // Cleanup
    return () => {
      cyInstance.off('viewport', handleViewportChange);
      cyInstance.off('add remove', renderMiniMap);
      cyInstance.off('data', renderMiniMap);
      cancelAnimationFrame(animationFrameId);
    };
  }, [cyInstance, visible, width, height]);

  // Handle click-to-navigate
  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!cyInstance || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;

    // Get graph bounds
    const boundingBox = cyInstance.elements().boundingBox();
    const graphWidth = boundingBox.x2 - boundingBox.x1 || 1;
    const graphHeight = boundingBox.y2 - boundingBox.y1 || 1;

    // Calculate scale
    const scaleX = width / graphWidth;
    const scaleY = height / graphHeight;
    const scale = Math.min(scaleX, scaleY) * 0.9;

    // Calculate offset
    const offsetX = (width - graphWidth * scale) / 2 - boundingBox.x1 * scale;
    const offsetY = (height - graphHeight * scale) / 2 - boundingBox.y1 * scale;

    // Convert click coordinates to graph coordinates
    const graphX = (clickX - offsetX) / scale;
    const graphY = (clickY - offsetY) / scale;

    // Calculate the pan position to center the clicked point
    const currentZoom = cyInstance.zoom();
    const containerWidth = cyInstance.width();
    const containerHeight = cyInstance.height();

    // Calculate pan to center the target position
    const panX = containerWidth / 2 - graphX * currentZoom;
    const panY = containerHeight / 2 - graphY * currentZoom;

    // Pan to clicked position with smooth animation
    cyInstance.animate({
      pan: { x: panX, y: panY },
      duration: 500,
      easing: 'ease-out',
    } as any);

    console.log('MINI-MAP: Navigated to position', { graphX, graphY, panX, panY });
  };

  if (!visible) {
    // Show just the toggle button when hidden
    return (
      <div
        style={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          zIndex: 1000,
        }}
      >
        <Tooltip title="Show mini-map">
          <IconButton
            size="small"
            onClick={handleToggleVisibility}
            style={{
              backgroundColor: '#fff',
              boxShadow: '0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)',
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
    );
  }

  return (
    <Paper
      elevation={3}
      style={{
        position: 'absolute',
        bottom: 16,
        right: 16,
        width: width,
        overflow: 'hidden',
        borderRadius: 4,
        zIndex: 1000,
      }}
    >
      {/* Header with toggle button */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingLeft: 8,
          paddingRight: 8,
          paddingTop: 4,
          paddingBottom: 4,
          backgroundColor: '#325BA8',
          color: '#fff',
        }}
      >
        <div style={{ fontSize: '11px', fontWeight: 600 }}>Overview</div>
        <Tooltip title="Hide mini-map">
          <IconButton
            size="small"
            onClick={handleToggleVisibility}
            style={{
              color: 'inherit',
              padding: 2,
            }}
          >
            <VisibilityOffIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        onClick={handleCanvasClick}
        style={{
          display: 'block',
          cursor: 'pointer',
        }}
      />
    </Paper>
  );
};

export default GraphMiniMap;
